import { chatAuthMayExist, loadChatSdk } from './chatFirebase';
import { businessCodesOfCustomToken, normalizeBusinessCodes, sameBusinessCodes } from './chatIdentity';
import { CHAT_TYPE, CHAT_TYPE_IDS, ENTITY_TYPE, firstName } from './channels';

const OPEN_STATUSES = ['OPEN', 'IN_PROGRESS'];
const BUSINESS_CODES_PER_QUERY = 15;
const FIELD_PATH_RESERVED = /[.~*/[\]]/;
const TYPING_OFF_TIMEOUT_MS = 2000;

const noop = () => undefined;

const toError = (e) => (e instanceof Error ? e : new Error(String(e?.message ?? e)));

const chatError = (code, message) => Object.assign(new Error(message), { code });

const sessionEndedError = () => chatError('chat/session-ended', 'The chat session has ended');

const noIdentityError = () => chatError('chat/no-identity', 'The token carries no chat identity');

const chatCollection = (typeId) => `typeId:${typeId}`;

const businessCodeChunks = (businessCodes) => {
	const chunks = [];
	for (let i = 0; i < businessCodes.length; i += BUSINESS_CODES_PER_QUERY) {
		chunks.push(businessCodes.slice(i, i + BUSINESS_CODES_PER_QUERY));
	}
	return chunks;
};

let authQueue = Promise.resolve();
let authTasks = 0;
let pendingSignIn = null;
let verifiedCodes = null;
let generation = 0;
const sessionTokens = new Set();
const endedTokens = new Set();
const typingChats = new Map();

const runExclusive = (task) => {
	const run = authQueue.then(async () => {
		authTasks += 1;
		try {
			return await task();
		} finally {
			authTasks -= 1;
		}
	});
	authQueue = run.catch(noop);
	return run;
};

const authSettled = async () => {
	let queue;
	do {
		queue = authQueue;
		await queue;
	} while (queue !== authQueue);
};

const currentUserOf = async (auth) => {
	await auth.authStateReady();
	return auth.currentUser;
};

const claimedBusinessCodes = async (authSdk, user) => {
	const { claims } = await authSdk.getIdTokenResult(user);
	return normalizeBusinessCodes(claims?.businessCodes);
};

const endSession = () => {
	sessionTokens.forEach((token) => endedTokens.add(token));
	sessionTokens.clear();
	pendingSignIn = null;
	verifiedCodes = null;
	generation += 1;
	typingChats.clear();
};

const signInAs = async (fToken) => {
	const businessCodes = businessCodesOfCustomToken(fToken);
	if (!businessCodes) {
		throw noIdentityError();
	}
	if (endedTokens.has(fToken)) {
		throw sessionEndedError();
	}
	const { auth, authSdk } = await loadChatSdk();
	const user = await currentUserOf(auth);
	let claimed = user ? await claimedBusinessCodes(authSdk, user).catch(() => null) : null;
	if (!sameBusinessCodes(claimed, businessCodes)) {
		if (user) {
			await authSdk.signOut(auth).catch(noop);
		}
		const credential = await authSdk.signInWithCustomToken(auth, fToken);
		claimed = await claimedBusinessCodes(authSdk, credential.user);
	}
	if (endedTokens.has(fToken)) {
		throw sessionEndedError();
	}
	if (!sameBusinessCodes(claimed, businessCodes)) {
		throw noIdentityError();
	}
	verifiedCodes = claimed;
	return claimed;
};

export const ensureChatAuth = (fToken) => {
	if (!fToken || endedTokens.has(fToken)) {
		return Promise.reject(sessionEndedError());
	}
	if (pendingSignIn && pendingSignIn.fToken === fToken) {
		return pendingSignIn.promise;
	}
	sessionTokens.add(fToken);
	const entry = { fToken, promise: runExclusive(() => signInAs(fToken)) };
	const clear = () => {
		if (pendingSignIn === entry) {
			pendingSignIn = null;
		}
	};
	entry.promise.then(clear, clear);
	pendingSignIn = entry;
	return entry.promise;
};

export const startChatAuth = (fToken) => {
	const businessCodes = businessCodesOfCustomToken(fToken);
	sessionTokens.forEach((token) => {
		if (!sameBusinessCodes(businessCodesOfCustomToken(token), businessCodes)) {
			endedTokens.add(token);
			sessionTokens.delete(token);
		}
	});
	return ensureChatAuth(fToken);
};

export const resumeChatAuth = () => {
	const resumed = generation;
	return runExclusive(async () => {
		const { auth, authSdk } = await loadChatSdk();
		const user = await currentUserOf(auth);
		const claimed = user ? await claimedBusinessCodes(authSdk, user) : null;
		if (generation !== resumed) {
			throw sessionEndedError();
		}
		if (claimed) {
			verifiedCodes = claimed;
		}
		return claimed;
	});
};

export const signOutChat = () => {
	const codes = verifiedCodes;
	const typing = codes ? [...typingChats.values()] : [];
	endSession();
	return runExclusive(async () => {
		verifiedCodes = null;
		if (!(await chatAuthMayExist())) {
			return;
		}
		const { auth, authSdk, db, firestoreSdk: fs } = await loadChatSdk();
		const user = await currentUserOf(auth);
		if (!user) {
			return;
		}
		if (
			typing.length > 0 &&
			sameBusinessCodes(await claimedBusinessCodes(authSdk, user).catch(() => null), codes)
		) {
			const writes = typing.map(({ typeId, chatId, businessCode }) =>
				fs
					.updateDoc(fs.doc(db, chatCollection(typeId), chatId), {
						typingUsers: fs.arrayRemove(businessCode),
					})
					.catch(noop)
			);
			await Promise.race([
				Promise.all(writes),
				new Promise((resolve) => setTimeout(resolve, TYPING_OFF_TIMEOUT_MS)),
			]);
		}
		await authSdk.signOut(auth);
	}).catch(noop);
};

const checkAuthLost = async (auth, authSdk) => {
	await authSettled();
	const expected = verifiedCodes;
	if (!expected) {
		return false;
	}
	let claimed = null;
	if (auth.currentUser) {
		try {
			claimed = await claimedBusinessCodes(authSdk, auth.currentUser);
		} catch {
			return false;
		}
	}
	if (authTasks > 0 || verifiedCodes !== expected || sameBusinessCodes(claimed, expected)) {
		return false;
	}
	endSession();
	return true;
};

export const onChatAuthChanged = (onLost, onOther) =>
	loadChatSdk().then(({ auth, authSdk }) => {
		authSdk.onIdTokenChanged(auth, () => {
			checkAuthLost(auth, authSdk).then((lost) => (lost ? onLost() : onOther()), noop);
		});
	});

const verifiedSdk = async () => {
	await authSettled();
	const expected = verifiedCodes;
	if (!expected) {
		throw sessionEndedError();
	}
	const sdk = await loadChatSdk();
	const user = sdk.auth.currentUser;
	if (!user || !sameBusinessCodes(await claimedBusinessCodes(sdk.authSdk, user), expected)) {
		throw sessionEndedError();
	}
	if (verifiedCodes !== expected) {
		throw sessionEndedError();
	}
	return { ...sdk, businessCodes: expected };
};

const chatSdkFor = async (businessCodes) => {
	const wanted = normalizeBusinessCodes(businessCodes);
	const sdk = await verifiedSdk();
	if (!sameBusinessCodes(sdk.businessCodes, wanted)) {
		throw sessionEndedError();
	}
	return sdk;
};

const asString = (value) => {
	if (typeof value === 'string') {
		return value;
	}
	return typeof value === 'number' && Number.isFinite(value) ? String(value) : '';
};

const asNumber = (value) => {
	if (typeof value === 'number') {
		return value;
	}
	return typeof value === 'string' ? parseInt(value, 10) || 0 : 0;
};

const asRecord = (value) => (value && typeof value === 'object' ? value : null);

const joinName = (...parts) =>
	parts
		.map((part) => asString(part).trim())
		.filter(Boolean)
		.join(' ');

const orderCodeOf = (data) => asString(asRecord(data.orderRef)?.orderKey) || '0';

// The order the chat was opened from: its total, delivery or takeaway, and payment method.
const orderSummaryOf = (data) => {
	const total = typeof data.orderTotal === 'string' ? parseFloat(data.orderTotal) : data.orderTotal;
	return {
		total: Number.isFinite(total) ? total : 0,
		type: asString(data.orderType),
		payment: asString(data.paymentMethod),
	};
};

const isOpenStatus = (status) => OPEN_STATUSES.includes(asString(status));

const isOwnedBy = (data, businessCodes) => businessCodes.includes(asString(data.businessCode));

export const isMine = (message) => asNumber(message.entityType) === ENTITY_TYPE.BUSINESS;

export const isUnreadIncoming = (message) => !isMine(message) && asNumber(message.readStatus) !== 1;

export const decodeMessages = (raw) => {
	const map = asRecord(raw);
	if (!map) {
		return [];
	}
	const messages = [];
	Object.entries(map).forEach(([id, value]) => {
		const m = asRecord(value);
		if (m) {
			messages.push({
				id,
				text: asString(m.text),
				imageUrl: asString(m.imageUrl),
				date: asString(m.cDate),
				entityType: asNumber(m.entityType),
				entityName: asString(m.entityName),
				entityImage: asString(m.entityImage),
				readStatus: asNumber(m.readStatus),
			});
		}
	});
	return messages;
};

const compareMessages = (a, b) => a.date.localeCompare(b.date) || a.id.localeCompare(b.id);

const latestMessage = (messages, match = () => true) => {
	let latest = null;
	messages.forEach((message) => {
		if (match(message) && (!latest || compareMessages(message, latest) > 0)) {
			latest = message;
		}
	});
	return latest;
};

const decodeTypingUsers = (raw) => (Array.isArray(raw) ? raw.filter((u) => typeof u === 'string' && u !== '') : []);

const withSenderFallback = (counterpart, messages, entityType) => {
	const named = counterpart.name
		? null
		: latestMessage(messages, (m) => m.entityType === entityType && m.entityName.trim() !== '');
	const pictured = counterpart.image
		? null
		: latestMessage(messages, (m) => m.entityType === entityType && m.entityImage !== '');
	return {
		name: counterpart.name || (named ? named.entityName.trim() : ''),
		image: counterpart.image || (pictured ? pictured.entityImage : ''),
	};
};

export const counterpartOf = (typeId, data, messages) => {
	switch (typeId) {
		case CHAT_TYPE.CUSTOMER:
			return withSenderFallback(
				{ name: joinName(data.referenceName), image: asString(data.referenceImage) },
				messages,
				ENTITY_TYPE.CUSTOMER
			);
		case CHAT_TYPE.DRIVER: {
			const named = latestMessage(
				messages,
				(m) => m.entityType === ENTITY_TYPE.DRIVER && m.entityName.trim() !== ''
			);
			return withSenderFallback(
				{
					name: named ? named.entityName.trim() : firstName(data.driverName),
					image: asString(data.driverImage),
				},
				messages,
				ENTITY_TYPE.DRIVER
			);
		}
		default:
			return { name: joinName(data.assigneeName, data.assigneeSurname), image: asString(data.assigneeImage) };
	}
};

const othersTypingOf = (data) => {
	const own = asString(data.businessCode);
	return decodeTypingUsers(data.typingUsers).some((u) => u !== own);
};

export const toThread = (typeId, chatId, data) => {
	const messages = decodeMessages(data.chatMessages).sort(compareMessages);
	return {
		id: chatId,
		typeId,
		key: asString(data.key),
		open: isOpenStatus(data.status),
		orderCode: orderCodeOf(data),
		businessCode: asString(data.businessCode),
		businessName: asString(data.businessName).trim(),
		counterpart: counterpartOf(typeId, data, messages),
		messages,
		othersTyping: othersTypingOf(data),
	};
};

export const toOpenChat = (typeId, chatId, data) => {
	const messages = decodeMessages(data.chatMessages);
	const lastMessage = latestMessage(messages);
	return {
		chatId,
		typeId,
		key: asString(data.key),
		orderCode: orderCodeOf(data),
		businessCode: asString(data.businessCode),
		businessName: asString(data.businessName).trim(),
		order: orderSummaryOf(data),
		counterpart: counterpartOf(typeId, data, messages),
		lastMessage,
		lastActivity: lastMessage?.date || asString(data.lastMessageDate) || asString(data.cDate),
		unreadCount: messages.filter(isUnreadIncoming).length,
		othersTyping: othersTypingOf(data),
	};
};

const byNewestActivity = (a, b) => b.lastActivity.localeCompare(a.lastActivity);

export const watchOpenChats = ({ businessCodes, onChange, onError }) => {
	let stopped = false;
	let unsubscribes = [];
	const byQuery = new Map();
	const fail = (error) => {
		if (!stopped) {
			onError(toError(error));
		}
	};
	chatSdkFor(businessCodes)
		.then(({ db, firestoreSdk: fs, businessCodes: codes }) => {
			if (stopped) {
				return;
			}
			const chunks = businessCodeChunks(codes);
			const listeners = CHAT_TYPE_IDS.length * chunks.length;
			unsubscribes = CHAT_TYPE_IDS.flatMap((typeId) =>
				chunks.map((chunkCodes, chunk) =>
					fs.onSnapshot(
						fs.query(
							fs.collection(db, chatCollection(typeId)),
							fs.where('businessCode', 'in', chunkCodes),
							fs.where('status', 'in', OPEN_STATUSES)
						),
						(snapshot) => {
							if (stopped) {
								return;
							}
							byQuery.set(
								`${typeId}:${chunk}`,
								snapshot.docs.map((d) => toOpenChat(typeId, d.id, d.data()))
							);
							onChange([...byQuery.values()].flat().sort(byNewestActivity), {
								complete: byQuery.size === listeners,
							});
						},
						fail
					)
				)
			);
		})
		.catch(fail);
	return () => {
		stopped = true;
		unsubscribes.forEach((unsubscribe) => unsubscribe());
		unsubscribes = [];
	};
};

export const watchChat = ({ typeId, chatId, businessCodes, onChange, onError }) => {
	let stopped = false;
	let unsubscribe = noop;
	const fail = (error) => {
		if (!stopped) {
			onError(toError(error));
		}
	};
	chatSdkFor(businessCodes)
		.then(({ db, firestoreSdk: fs, businessCodes: codes }) => {
			if (stopped) {
				return;
			}
			unsubscribe = fs.onSnapshot(
				fs.doc(db, chatCollection(typeId), chatId),
				(snapshot) => {
					if (stopped) {
						return;
					}
					const data = snapshot.data();
					if (!data) {
						onChange(null);
					} else if (isOwnedBy(data, codes)) {
						onChange(toThread(typeId, snapshot.id, data));
					}
				},
				fail
			);
		})
		.catch(fail);
	return () => {
		stopped = true;
		unsubscribe();
		unsubscribe = noop;
	};
};

const docActivity = (d) => {
	const data = d.data();
	return asString(data.lastMessageDate) || asString(data.cDate);
};

export const findOpenChatId = async ({ businessCodes, typeId, key }) => {
	const { db, firestoreSdk: fs, businessCodes: codes } = await chatSdkFor(businessCodes);
	const snapshots = await Promise.all(
		businessCodeChunks(codes).map((chunkCodes) =>
			fs.getDocs(
				fs.query(
					fs.collection(db, chatCollection(typeId)),
					fs.where('businessCode', 'in', chunkCodes),
					fs.where('key', '==', key),
					fs.where('status', 'in', OPEN_STATUSES)
				)
			)
		)
	);
	const newest = snapshots
		.flatMap((snapshot) => snapshot.docs)
		.sort((a, b) => docActivity(b).localeCompare(docActivity(a)))[0];
	return newest ? newest.id : null;
};

export const markChatRead = async ({ typeId, chatId, messageIds }) => {
	const ids = (Array.isArray(messageIds) ? messageIds : []).filter(
		(id) => typeof id === 'string' && id !== '' && !FIELD_PATH_RESERVED.test(id)
	);
	if (ids.length === 0) {
		return;
	}
	await verifiedSdk()
		.then(({ db, firestoreSdk: fs }) => {
			const fieldsAndValues = ids.flatMap((id) => [new fs.FieldPath('chatMessages', id, 'readStatus'), 1]);
			return fs.updateDoc(fs.doc(db, chatCollection(typeId), chatId), ...fieldsAndValues);
		})
		.catch(noop);
};

export const setChatTyping = async ({ typeId, chatId, businessCode, typing }) => {
	if (!businessCode) {
		return;
	}
	const key = `${typeId}/${chatId}`;
	if (typing) {
		typingChats.set(key, { typeId, chatId, businessCode });
	} else {
		typingChats.delete(key);
	}
	await verifiedSdk()
		.then(({ db, firestoreSdk: fs }) =>
			fs.updateDoc(fs.doc(db, chatCollection(typeId), chatId), {
				typingUsers: typing ? fs.arrayUnion(businessCode) : fs.arrayRemove(businessCode),
			})
		)
		.catch(noop);
};

export const isChatRated = async ({ typeId, chatId }) => {
	const { db, firestoreSdk: fs } = await verifiedSdk();
	const snapshot = await fs.getDoc(fs.doc(db, chatCollection(typeId), chatId));
	const data = snapshot.data();
	return asNumber(asRecord(data && data.rates)?.[ENTITY_TYPE.BUSINESS]) > 0;
};
